import { useWallet } from '@/contexts/WalletContext';
import React, { useState, useEffect, useTransition, useRef, useCallback } from 'react';
import Layout from '@/components/Layout';
import Home from '@/components/Home';
import Dashboard from '@/components/Dashboard';
import { useToast } from '@/contexts/ToastContext';
import { useData, transformRosterMemberToArtistProfile } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLogger } from './contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import SystemLogPage from '@/pages/SystemLogPage';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { API_BASE_URL } from '@/config';
import {
  UserRole,
  ArtistProfile as IArtistProfile,
  Artist,
  OnboardingRequest,
  OnboardingStatus,
  Lead,
  RosterMember,
  SupportTicketCategory,
  SupportTicketPriority,
  UserStatus,
} from '@/types';
import {
  Bell,
  Search,
  LogOut,
  Loader2,
  CheckCircle,
  ShieldAlert,
  AlertTriangle,
  RefreshCw,
  ArrowLeft,
} from 'lucide-react';
import {
  MOCK_ARTIST_PROFILE,
  MOCK_ROSTER,
  MOCK_USERS_BY_ROLE,
} from '@/mockData';
import { Routes, Route, useLocation, useNavigate as useReactRouterNavigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useWalletLogin } from '@/hooks/useWalletLogin'; // Import the hook

// Statically Imported Components
import Announcements from '@/components/Announcements';
import ChatOverlay from '@/components/ChatOverlay';
import SearchResults from '@/components/SearchResults';
import Sitemap from '@/components/Sitemap';
import SystemDiagrams from '@/components/SystemDiagrams';
import WhitePaper from '@/components/WhitePaper';
import TokenExchange from '@/components/TokenExchange';
import BookingHub from '@/components/BookingHub';
import DaoGovernance from '@/components/DaoGovernance';
import Marketplace from '@/components/Marketplace';
import ServicesHub from '@/components/ServicesHub';
import AnalyticsDashboard from '@/components/AnalyticsDashboard';
import ArtistProfile from '@/components/ArtistProfile';
import Roster from '@/components/Roster';
import JoinForm from '@/components/JoinForm';
import AdminLeads from '@/components/AdminLeads';
import AdminEmailTemplates from '@/components/AdminEmailTemplates';
import TreasuryPage from '@/pages/TreasuryPage';
import HRDashboard from '@/components/HRDashboard';
import Forum from '@/components/Forum';
import CreativeStudio from '@/components/CreativeStudio';
import MembershipPlans from '@/components/MembershipPlans';
import MyNetwork from '@/components/MyNetwork';
import AdminReview from '@/components/AdminReview';
import ContractsDashboard from '@/components/ContractsDashboard';
import AdminPage from '@/pages/AdminPage';
import AdminModeration from '@/components/AdminModeration';
import TablesPage from '@/pages/TablesPage';
import SupportRequestsPage from '@/pages/SupportRequestsPage';
import OnboardingPage from '@/pages/OnboardingPage';
import RosterAnalyticsPage from '@/pages/RosterAnalyticsPage';
import MySupportTickets from '@/components/MySupportTickets';
import HRDPage from '@/pages/HRDPage';
import PendingPage from '@/pages/Pending';
import UrgentBookingsPage from '@/pages/UrgentBookingsPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import EventPage from '@/pages/EventPage';

const PageLoader = () => (
  <div className="h-[60vh] w-full flex flex-col items-center justify-center text-kala-400">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-kala-800 border-t-kala-secondary rounded-full animate-spin"></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-2 h-2 bg-kala-secondary rounded-full"></div>
      </div>
    </div>
    <p className="text-sm font-bold mt-6 tracking-widest uppercase animate-pulse">
      Loading KalaKrut Network...
    </p>
  </div>
);

const BlockedScreen: React.FC<{ onAppeal: (reason: string) => void }> = ({
  onAppeal,
}) => {
  const [appealReason, setAppealReason] = useState('');
  const [hasAppealed, setHasAppealed] = useState(false);
  const { notify } = useToast();
  const { addLog } = useLogger();

  const handleSubmit = async () => {
    if (!appealReason.trim()) return;
    await onAppeal(appealReason);
    setHasAppealed(true);
  };

  return (
    <div className="min-h-screen bg-kala-900 flex flex-col items-center justify-center p-4 text-center">
      <div className="bg-red-900/20 p-8 rounded-full border-4 border-red-500 mb-8 animate-pulse">
        <ShieldAlert className="w-24 h-24 text-red-500" />
      </div>
      <h1 className="text-4xl font-bold text-white mb-4">Account Suspended</h1>
      <div className="max-w-xl mx-auto space-y-4 mb-8">
        <p className="text-red-200 text-lg">
          Your account has been flagged for a potential violation of our platform policies.
        </p>
        <p className="text-kala-400 text-sm">
          If you believe this is an error, you may submit an appeal to our moderation team.
        </p>
      </div>
      {!hasAppealed ? (
        <div className="w-full max-w-md bg-kala-800 p-6 rounded-xl border border-kala-700">
          <h3 className="text-white font-bold mb-3 flex items-center justify-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" /> File Formal
            Appeal
          </h3>
          <textarea
            className="w-full bg-kala-900 border border-kala-600 rounded-lg p-3 text-white text-sm mb-4 outline-none focus:border-kala-secondary"
            rows={4}
            placeholder="Explain why this suspension was an error..."
            value={appealReason}
            onChange={(e) => setAppealReason(e.target.value)}
          />
          <button
            onClick={handleSubmit}
            disabled={!appealReason}
            className="w-full px-8 py-3 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-xl border border-kala-500 transition-colors disabled:opacity-50"
          >
            Submit Appeal to Admins
          </button>
        </div>
      ) : (
        <div className="bg-green-900/20 border border-green-500/30 p-6 rounded-xl max-w-md">
          <div className="text-green-400 font-bold text-lg mb-2 flex items-center justify-center gap-2">
            <CheckCircle className="w-5 h-5" /> Appeal Submitted
          </div>
          <p className="text-green-200 text-sm">
            Our moderation team will review your request. You will be notified of the outcome via email.
          </p>
        </div>
      )}
    </div>
  );
};


const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useReactRouterNavigate();
  const { addLog } = useLogger();
  const { isConnected: isWalletConnected, address: walletAddress, open, disconnect, isConnecting } = useWallet();
  const { notify } = useToast();
  const { updateUser } = useData();
  const { currentUser, loading: authLoading, login, logout, setCurrentUser, setToken, setIsNewWeb3User } = useAuth(); // DEFINITIVE FIX: Get the REAL logout function from the context
  const { processWalletLogin, isLoggingIn: isWalletLoggingIn } = useWalletLogin();
  const [isPending, startTransition] = useTransition();
  const [isWeb2LoggingIn, setIsWeb2LoggingIn] = useState(false);
  const [walletConnectionStatus, setWalletConnectionStatus] = useState<'idle' | 'connecting' | 'connected' | 'failed'>('idle');
  const [showChat, setShowChat] = useState(false);
  const [chatRecipient, setChatRecipient] = useState(MOCK_ARTIST_PROFILE);
  const [showTokenExchange, setShowTokenExchange] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProfile, setSelectedProfile] = useState<RosterMember | IArtistProfile>(MOCK_ARTIST_PROFILE);
  const [isUserBlocked, setIsUserBlocked] = useState(false);
  const [onboardingRequests, setOnboardingRequests] = useState<OnboardingRequest[]>([]);
  const [aiLeads, setAiLeads] = useState<Lead[]>([]);
  const [onboardingFilter, setOnboardingFilter] = useState<'all' | 'Lead' | 'Join Request'>('all');
  const [profileTab, setProfileTab] = useState('overview');
  const [joinRequestSubmitted, setJoinRequestSubmitted] = useState(false);

  // Effect to handle wallet login flow
  useEffect(() => {
    const walletLoginFlow = async () => {
      if (isWalletConnected && walletAddress && !currentUser && !isWalletLoggingIn) {
        // Prevent processing if already on the join page, which is the destination for new users.
        if (location.pathname === '/join_request') return;

        const redirectPath = await processWalletLogin();
        if (redirectPath) {
          startTransition(() => {
            navigate(redirectPath);
          });
        }
        // For existing users, processWalletLogin handles setting the currentUser.
        // The routing guard effect below will then handle the redirect to the dashboard.
      }
    };
    walletLoginFlow();
  }, [isWalletConnected, walletAddress, currentUser, isWalletLoggingIn, processWalletLogin, navigate, location.pathname]);

  // DEFINITIVE FIX 1: This effect now uses a blank profile template to prevent data leakage from mock data.
  // DEFINITIVE FIX 2: It also checks for an incomplete profile and shows a notification to guide the user.
  useEffect(() => {
    if (currentUser) {
      // 1. Create a truly blank profile template, preventing mock data leakage.
      const blankProfile: IArtistProfile = {
        id: '',
        name: '',
        avatar: '',
        role: currentUser.role,
        description: '',
        location: '',
        genres: [],
        verified: false,
        pressKit: { photos: [], topTracks: [], techRiderUrl: '', socials: [] },
        stats: { gigsCompleted: 0, activeGigs: 0, rating: 0, responseTime: '' },
        isMock: false,
        xp: 0,
        level: 0,
        coverImage: '',
      };

      const realProfile = transformRosterMemberToArtistProfile(currentUser, blankProfile);
      setSelectedProfile(realProfile);

      // 2. Check for incomplete profile and notify the user.
      const isProfileIncomplete = !realProfile.bio || realProfile.genres.length === 0;
      if (isProfileIncomplete) {
        const notificationId = `incomplete-profile-${currentUser.id}`;
        notify(
          'Your profile is incomplete. Update it now to get the most out of KalaKrut!',
          'warn',
          {
            duration: Infinity, // Keep the notification until dismissed
            id: notificationId,
            button: {
              text: 'Update Profile',
              action: () => {
                navigate('/profile');
                // You might want to dismiss the notification here if the toast system allows
              },
            },
          }
        );
      }
    }
  }, [currentUser, navigate, notify]);


  useEffect(() => {
    const autoLogin = async () => {
      if (currentUser) return;
  
      const urlParams = new URLSearchParams(location.search);
      const tokenFromUrl = urlParams.get('token');

      if (tokenFromUrl) {
        addLog('Found token in URL, attempting to log in.', LogLevel.INFO, LogCategory.AUTH, { token: tokenFromUrl });
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
                headers: { 'Authorization': `Bearer ${tokenFromUrl}` },
                credentials: 'include',
            });
            const data = await response.json();
            if (response.ok && data.user) {
                addLog('Successfully authenticated with token from URL.', LogLevel.SUCCESS, LogCategory.AUTH, { userId: data.user.id });
                setToken(tokenFromUrl); // Set token in AuthContext
                setCurrentUser(data.user);
                navigate('/dashboard', { replace: true });
            } else {
                throw new Error(data.message || 'Invalid token from URL');
            }
        } catch (error) {
            addLog('Failed to authenticate with token from URL.', LogLevel.ERROR, LogCategory.AUTH, { error });
            notify('Login failed: The link may be invalid or expired.', 'error');
            navigate('/', { replace: true });
        }
        return; // End execution here
      }

      const email = urlParams.get('email');
      const role = urlParams.get('role') as UserRole;
      const requiresPasswordSetup = urlParams.get('requiresPasswordSetup') === 'true';
  
      if (email && urlParams.get('token') && role) {
        addLog('Attempting auto-login with email, token, and role from URL.', LogLevel.INFO, LogCategory.AUTH, { email, role });
        
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/verify-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, token: urlParams.get('token'), role }),
            credentials: 'include',
          });
  
          const data = await response.json();
  
          if (response.ok && data.user) {
            addLog('Token verification successful, logging in user.', LogLevel.SUCCESS, LogCategory.AUTH, { userId: data.user.id });
            setCurrentUser(data.user);
            
            if (requiresPasswordSetup) {
              addLog('New user requires password setup. Redirecting to reset password page.', LogLevel.INFO, LogCategory.AUTH, { userId: data.user.id });
              navigate(`/auth/reset-password?email=${encodeURIComponent(email)}&token=${encodeURIComponent(urlParams.get('token'))}`, { replace: true });
            } else {
              // Use replace to avoid polluting browser history and navigate to dashboard
              navigate('/dashboard', { replace: true });
            }
          } else {
            addLog('Token verification failed.', LogLevel.WARN, LogCategory.AUTH, { email, error: data.message });
            notify(data.message || 'Automatic login failed. The link may be invalid or expired.', 'error');
            navigate('/', { replace: true });
          }
        } catch (error) {
          addLog('Network error during auto-login.', LogLevel.ERROR, LogCategory.AUTH, { error });
          notify('An error occurred during automatic login.', 'error');
          navigate('/', { replace: true });
        }
      }
    };
  
    autoLogin();
  }, [currentUser, location, addLog, notify, setCurrentUser, navigate, setToken]);

  // THIS IS THE ROUTING GUARD THAT REDIRECTS LOGGED-IN USERS TO THE DASHBOARD
  useEffect(() => {
    const publicViews = ['/', '/announcements_public', '/join_request', '/pending', '/auth/reset-password'];
    
    // Guard for non-logged-in users trying to access protected routes
    if (!currentUser && !publicViews.includes(location.pathname)) {
      startTransition(() => {
        addLog(`Public view guard: Not logged in, redirecting from "${location.pathname}" to home.`, LogLevel.INFO, LogCategory.NAVIGATION);
        navigate('/');
      });
    } 
    // Guard for logged-in users who land on a public page (e.g., after login)
    else if (currentUser && (location.pathname === '/' || location.pathname === '/join_request')) {
      startTransition(() => {
        addLog(`Public view guard: Logged in, redirecting from "${location.pathname}" to dashboard.`, LogLevel.INFO, LogCategory.NAVIGATION);
        navigate('/dashboard');
      });
    }
  }, [currentUser, location.pathname, addLog, navigate, startTransition]);
  
    useEffect(() => {
      const fetchOnboardingRequests = async () => {
        if (currentUser?.isMock) {
            addLog('Skipping onboarding fetch for demo user', LogLevel.INFO, LogCategory.DATA);
            return; 
        }
        addLog('Fetching onboarding requests (leads and join requests)', LogLevel.INFO, LogCategory.DATA);
        try {
          const [leadsResponse, joinRequestsResponse] = await Promise.all([
            fetch(`${API_BASE_URL}/api/leads`, { credentials: 'include' }),
            fetch(`${API_BASE_URL}/api/join-requests/admin/all`, { credentials: 'include' })
          ]);
  
          let leadsData: Lead[] = [];
          if (leadsResponse.ok) {
            leadsData = await leadsResponse.json();
            addLog('Successfully fetched leads data', LogLevel.INFO, LogCategory.DATA, { count: leadsData.length });
          } else {
            const errorText = await leadsResponse.text();
            addLog('Failed to fetch leads data', LogLevel.ERROR, LogCategory.API, { status: leadsResponse.status, error: errorText });
            notify('Failed to fetch leads data.', 'error');
          }
  
          let joinRequestsData: OnboardingRequest[] = [];
          if (joinRequestsResponse.ok) {
            joinRequestsData = await joinRequestsResponse.json();
            addLog('Successfully fetched join requests data', LogLevel.INFO, LogCategory.DATA, { count: joinRequestsData.length });
          } else {
            const errorText = await joinRequestsResponse.text();
            addLog('Failed to fetch join requests data', LogLevel.ERROR, LogCategory.API, { status: joinRequestsResponse.status, error: errorText });
            notify('Failed to fetch join requests data.', 'error');
          }
  
          const transformedAiLeads = leadsData.map((lead) => ({
            id: lead.id,
            user: lead.user,
            userId: lead.userId,
            status: lead.status,
            createdAt: new Date(lead.createdAt),
            updatedAt: new Date(lead.updatedAt),
          }));
          setAiLeads(transformedAiLeads);
  
          const transformedOnboardingLeads: OnboardingRequest[] = leadsData.map((lead) => ({
            id: lead.id,
            name: lead.user ? lead.user.name ?? 'Unknown' : 'Unknown',
            email: lead.user ? lead.user.email ?? '' : '',
            type: 'Lead',
            date: new Date(lead.createdAt),
            status: lead.status as OnboardingStatus,
            requestedRole: lead.user ? lead.user.role : UserRole.GUEST,
            role: lead.user ? lead.user.role : UserRole.GUEST,
            location: lead.user ? lead.user.location ?? '' : '',
            bio: lead.user ? (lead.user.bio ?? '') : '',
        }));
  
          const transformedJoinRequests: OnboardingRequest[] = joinRequestsData.map((req) => ({
              id: req.id,
              name: req.name,
              email: req.email,
              type: 'Join Request',
              date: new Date(req.submittedAt!),
              status: req.status,
              requestedRole: req.role,
              role: req.role,
              location: req.location,
              bio: req.bio,
          }));
  
          setOnboardingRequests([...transformedOnboardingLeads, ...transformedJoinRequests]);
        } catch (error) {
          addLog('Network error fetching onboarding requests', LogLevel.ERROR, LogCategory.API, { error });
          notify('A network error occurred while fetching onboarding data.', 'error');
        }
      };
  
      if (currentUser && (currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SYSTEM_ADMIN_LIVE)) {
        fetchOnboardingRequests();
      }
    }, [currentUser, notify, addLog]);

    const handleLogin = async (
      role: UserRole | null,
      method: 'web2' | 'web3',
      credentials?: { email?: string; password?: string; isDemo?: boolean; }
    ) => {
      addLog(`Login attempt: role=${role}, method=${method}`, LogLevel.INFO, LogCategory.AUTH);

      // Keep the existing, correct Web3 logic
      if (method === 'web3') {
        addLog('Web3 login button clicked. Opening wallet modal if not connected.', LogLevel.INFO, LogCategory.AUTH);
        if (!isWalletConnected) {
            open(); // The useEffect will handle the rest after connection
        }
        return;
      }
  
      // --- REPLACEMENT FOR WEB2 LOGIN --- 
      // Delegate directly to the reliable login function from AuthContext
      if (role && credentials) {
          await login(role, 'web2', credentials);
      } else {
          notify('Role and credentials are required for login.', 'error');
      }
    };

  const addLead = (artist: Artist): Promise<boolean> => {
    return new Promise((resolve) => {
      setAiLeads((prevLeads) => {
        if (prevLeads.some((lead) => lead.id === artist.id)) {
          addLog('Add lead skipped: already exists', LogLevel.INFO, LogCategory.LEAD_MANAGEMENT, { artistId: artist.id, artistName: artist.name });
          notify(`${artist.name} is already in your leads list.`, 'info');
          resolve(false);
          return prevLeads;
        }

        const newAiLead: Lead = {
          id: artist.id,
          user: null,
          userId: artist.id,
          status: 'New',
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        addLog('Added new lead', LogLevel.SUCCESS, LogCategory.LEAD_MANAGEMENT, { leadId: newAiLead.id, name: artist.name });

        setOnboardingRequests((prevOnboarding) => {
            const newOnboardingRequest: OnboardingRequest = {
                id: artist.id,
                name: artist.name,
                email: '',
                user: {id: artist.id, name: artist.name, role: UserRole.ARTIST},
                userId: artist.id,
                type: 'Lead',
                date: new Date(),
                status: OnboardingStatus.PENDING,
                requestedRole: UserRole.ARTIST,
                submittedAt: new Date(),
            };
            if (prevOnboarding.some(r => r.id === newOnboardingRequest.id)) {
                return prevOnboarding;
            }
            return [newOnboardingRequest, ...prevOnboarding];
        });

        notify(`Successfully added ${artist.name} as a new lead.`, 'success');
        resolve(true);
        return [newAiLead, ...prevLeads];
      });
    });
  };

  const handleOnboardingStatusChange = async (id: string, newStatus: 'Approved' | 'Denied') => {
    const request = onboardingRequests.find(r => r.id === id);
    if (!request) return;

    const newOnboardingStatus = newStatus === 'Approved' ? OnboardingStatus.APPROVED : OnboardingStatus.REJECTED;

    addLog(`Updating onboarding status for request ${id} to ${newOnboardingStatus}`, LogLevel.INFO, LogCategory.ONBOARDING, { requestId: id, newStatus: newOnboardingStatus });

    let url = '';
    let options: RequestInit = { method: 'POST', credentials: 'include' }; 

    if (request.type === 'Lead') {
      url = `${API_BASE_URL}/api/leads/${id}`;
      options = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus.toUpperCase() }),
        credentials: 'include',
      };
    } else { // Join Request
      const action = newStatus === 'Approved' ? 'approve' : 'deny';
      url = `${API_BASE_URL}/api/join-requests/admin/${action}/${id}`;
    }

    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to update status to ${newStatus}`);
      }

      setOnboardingRequests(currentRequests =>
        currentRequests.map(req =>
          req.id === id ? { ...req, status: newOnboardingStatus } : req
        )
      );
      addLog(`Onboarding status updated successfully for request ${id}`, LogLevel.SUCCESS, LogCategory.ONBOARDING, { requestId: id, newStatus: newOnboardingStatus });
      notify(`Request ${id} has been ${newStatus.toLowerCase()}.`, 'success');
    } catch (error) {
        let message = 'An unknown error occurred.';
        if (error instanceof Error) {
            message = error.message;
        }
        addLog('Error updating onboarding status', LogLevel.ERROR, LogCategory.ONBOARDING, { requestId: id, error: message });
        notify(message, 'error');
        console.error(error);
    }
  };

  const handleOnboardingDelete = async (id: string, type: 'Lead' | 'Join Request') => {
    addLog(`Attempting to delete onboarding item ${id} of type ${type}`, LogLevel.INFO, LogCategory.ONBOARDING, { id, type });

    if (!currentUser || (currentUser.role !== UserRole.ADMIN && currentUser.role !== UserRole.SYSTEM_ADMIN_LIVE)) {
      addLog('Unauthorized attempt to delete onboarding item', LogLevel.WARN, LogCategory.SECURITY);
      notify('You are not authorized to perform this action.', 'error');
      return;
    }

    const url = type === 'Lead'
      ? `${API_BASE_URL}/api/leads/${id}`
      : `${API_BASE_URL}/api/join-requests/admin/${id}`;

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to delete ${type}`);
      }

      setOnboardingRequests(currentRequests =>
        currentRequests.filter(req => req.id !== id)
      );
      addLog(`Successfully deleted onboarding item ${id}`, LogLevel.SUCCESS, LogCategory.ONBOARDING, { id, type });
      notify(`${type} with ID ${id} has been deleted.`, 'success');
    } catch (error) {
      let message = 'An unknown error occurred.';
      if (error instanceof Error) {
        message = error.message;
      }
      addLog(`Error deleting onboarding item ${id}`, LogLevel.ERROR, LogCategory.ONBOARDING, { id, type, error: message });
      notify(message, 'error');
      console.error(error);
    }
  };


  const handleBlockUser = () => {
    if (!currentUser) return;
    addLog('User blocked due to policy violation', LogLevel.CRITICAL, LogCategory.SECURITY, { userId: currentUser.id, userName: currentUser.name });
    setIsUserBlocked(true);
    setShowChat(false);
    notify('Account suspended due to policy violation.', 'error');
  };

  const handleAppeal = async (reason: string) => {
    if (!currentUser) return;
    addLog('User appeal submitted', LogLevel.WARN, LogCategory.SECURITY, { userId: currentUser.id, reason });

    try {
      const response = await fetch(`${API_BASE_URL}/api/appeals`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
          credentials: 'include',
        }
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit appeal.');
      }

      notify(data.message || 'Your appeal has been submitted and will be reviewed by the moderation team.', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Could not submit your appeal. Please try again later.';
      addLog('Failed to submit user appeal', LogLevel.ERROR, LogCategory.SECURITY, { userId: currentUser.id, error: errorMessage });
      notify(errorMessage, 'error');
    }
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      addLog('Performing search', LogLevel.INFO, LogCategory.GENERAL, { query: searchQuery });
      navigate('/search_results');
    }
  };

  const handleOpenChat = () => {
    if ('name' in selectedProfile) {
        addLog('Opening chat', LogLevel.INFO, LogCategory.GENERAL, { recipientName: selectedProfile.name });
        setChatRecipient(selectedProfile as IArtistProfile);
        setShowChat(true);
    }
  };

  const handleViewProfile = (id: string) => {
    const member = MOCK_ROSTER.find((m) => m.id === id);
    if (member) {
      addLog('Viewing user profile', LogLevel.INFO, LogLevel.INFO, { profileId: id });
      setSelectedProfile(transformRosterMemberToArtistProfile(member, MOCK_ARTIST_PROFILE));
      setProfileTab('overview');
      navigate('/profile');
    }
  };

  const handleUpdateUserProfile = (updates: Partial<IArtistProfile>) => {
    if (!currentUser) return;
    
    addLog('Updating user profile', LogLevel.INFO, LogCategory.PROFILE, { userId: currentUser.id, updates });
    const updatedRosterMember: RosterMember = {
        ...currentUser,
        ...updates,
    };

    setCurrentUser(updatedRosterMember);
    updateUser(updatedRosterMember);
    
    if (selectedProfile.id === currentUser.id) {
      setSelectedProfile(transformRosterMemberToArtistProfile(updatedRosterMember, MOCK_ARTIST_PROFILE));
    }
  };

  const UserWidget = () => {
    if (!currentUser) return null;
    return (
      <div className="flex flex-col md:flex-row items-end md:items-center justify-between mb-8 gap-4">
        <div className="text-white">
          <h1 className="text-2xl font-bold">
            Welcome back, {currentUser.name}
          </h1>
          <p className="text-kala-400 text-sm">
            Role: {currentUser.role} &bull; {currentUser.location}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative hidden lg:block">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
              placeholder="Search portal..."
              className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-48 transition-all focus:w-64"
            />
          </div>
          {!isWalletConnected ? (
            <button
              onClick={open}
              disabled={isConnecting || isWeb2LoggingIn || isWalletLoggingIn}
              className="text-xs bg-kala-secondary text-kala-900 px-3 py-1.5 rounded font-bold hover:bg-cyan-400 transition-colors flex items-center gap-2"
            >
              {(isConnecting || isWeb2LoggingIn || isWalletLoggingIn) ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                'Connect Wallet'
              )}
            </button>
          ) : null}
          <button className="relative p-2 text-kala-400 hover:text-white transition-colors">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1.5 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
          <div className="flex items-center gap-3 pl-4 border-l border-kala-700">
            <div className="text-right hidden sm:block">
              {isWalletConnected ? (
                <div className="flex items-center gap-3">
                  <div>
                    <div className="text-sm font-bold text-white text-right font-mono tracking-tighter">
                      {walletAddress}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowTokenExchange(true)}
                    className="p-2 bg-kala-800 hover:bg-kala-700 rounded-full border border-kala-600 text-kala-secondary transition-colors"
                    title="Swap Tokens"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <></>
              )}
            </div>
            <div
              onClick={() => {
                setSelectedProfile(transformRosterMemberToArtistProfile(currentUser, MOCK_ARTIST_PROFILE));
                setProfileTab('overview');
                navigate('/profile');
              }}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-kala-secondary to-purple-600 p-0.5 cursor-pointer hover:scale-105 transition-transform ml-2"
              title="View My Profile"
            >
              <img
                src={currentUser.avatar}
                alt="Me"
                className="w-full h-full rounded-full border-2 border-kala-900 object-cover"
              />
            </div>
            <button
              onClick={logout} // DEFINITIVE FIX: Use the REAL logout function
              className="text-kala-500 hover:text-red-400 transition-colors ml-2"
              title="Log Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAppContent = () => {
    const path = location.pathname;
    let activeTableView = '';
    if (path.startsWith('/tables')) {
        activeTableView = path.substring(8);
    } else if (path === '/treasury') {
        activeTableView = 'treasury';
    } else if (path === '/onboarding') {
        activeTableView = 'onboarding';
    } else if (path === '/roster_management') {
        activeTableView = 'roster_management';
    } else if (path === '/roster_analytics') {
        activeTableView = 'roster_analytics';
    }

    const isTablesView = path.startsWith('/tables') || path === '/treasury' || path === '/onboarding' || path === '/roster_management' || path === '/roster_analytics';

    return (
      <div
        className={
          isPending
            ? 'opacity-70 transition-opacity pointer-events-none'
            : 'opacity-100 transition-opacity'
        }
      >
        <Routes>
          <Route path="/search_results" element={<SearchResults query={searchQuery} onNavigate={navigate} />} />
          <Route path="/sitemap" element={<Sitemap onNavigate={navigate} />} />
          <Route path="/system_log" element={<SystemLogPage currentUser={currentUser} />} />
          <Route path="/system_docs" element={(currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.SYSTEM_ADMIN_LIVE) ? <SystemDiagrams /> : <div>Access Denied</div>} />
          <Route path="/whitepaper" element={<WhitePaper />} />
          <Route path="/booking" element={<BookingHub onBlockUser={handleBlockUser} onOpenExchange={() => setShowTokenExchange(true)} currentUserId={currentUser?.id} />} />
          <Route path="/governance" element={<DaoGovernance currentUserRole={currentUser?.role || UserRole.GUEST} onOpenExchange={() => setShowTokenExchange(true)} currentUserId={currentUser?.id} />} />
          <Route path="/marketplace" element={<Marketplace onBlockUser={handleBlockUser} onChat={(seller) => { if('name' in selectedProfile){ setChatRecipient({ ...MOCK_ARTIST_PROFILE, name: seller.name, avatar: seller.avatar, }); setShowChat(true); } }} currentUserRole={currentUser?.role || UserRole.GUEST} currentUserId={currentUser.id} />} />
          <Route path="/services" element={<ServicesHub userRole={currentUser?.role || UserRole.GUEST} onNavigateToLeads={() => { setSelectedProfile(transformRosterMemberToArtistProfile(currentUser, MOCK_ARTIST_PROFILE)); setProfileTab('leads'); navigate('/profile'); }} onBlockUser={handleBlockUser} />} />
          <Route path="/roster" element={<Roster onNavigate={navigate} onViewProfile={handleViewProfile} currentUserId={currentUser?.id} />} />
          <Route path="/forum" element={<Forum onBlockUser={handleBlockUser} currentUserRole={currentUser?.role || UserRole.GUEST} currentUserId={currentUser.id} />} />
          <Route path="/studio" element={<CreativeStudio onBack={() => {}} onBlockUser={handleBlockUser} />} />
          <Route path="/admin_email_templates" element={(currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.SYSTEM_ADMIN_LIVE) ? <AdminEmailTemplates isDemoMode={false} /> : <div>Access Denied</div>} />
          <Route path="/admin_review" element={(currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.SYSTEM_ADMIN_LIVE) ? <AdminReview requests={onboardingRequests} onStatusChange={handleOnboardingStatusChange} /> : <div>Access Denied</div>} />
          <Route path="/contracts" element={<ErrorBoundary><ContractsDashboard /></ErrorBoundary>} />
          <Route path="/membership" element={<MembershipPlans currentUser={currentUser} />} />
          <Route path="/my_network" element={<MyNetwork currentUser={currentUser} />} />
          <Route path="/events" element={<EventPage currentUserRole={currentUser.role} currentUserId={currentUser.id}/>} />
          <Route path="/leads_and_ai" element={<AdminLeads />} />
          <Route path="/support-requests" element={(currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SYSTEM_ADMIN_LIVE) ? <SupportRequestsPage onNavigate={navigate} /> : <MySupportTickets onNavigate={navigate} />} />
          <Route path="/my-support-tickets" element={<MySupportTickets onNavigate={navigate} />} />
          <Route path="/hrd" element={(currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.DAO_GOVERNOR || currentUser.role === UserRole.SYSTEM_ADMIN_LIVE) ? <HRDashboard onNavigate={navigate} /> : <div>Access Denied</div>} />
          <Route path="/admin" element={(currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SYSTEM_ADMIN_LIVE) ? <AdminPage onNavigate={navigate} /> : <div>Access Denied</div>} />
          <Route path="/admin/moderation" element={(currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SYSTEM_ADMIN_LIVE) ? <AdminModeration /> : <div>Access Denied</div>} />
          {isTablesView && <Route path="/*" element={<TablesPage activeView={activeTableView} onNavigate={navigate} currentUserRole={currentUser.role} currentUserId={currentUser.id} requests={onboardingRequests} onStatusChange={handleOnboardingStatusChange} onDelete={handleOnboardingDelete}/>} />} 
          <Route path="/urgent_bookings" element={<UrgentBookingsPage onNavigate={navigate} />} />
          <Route path="/profile" element={<ArtistProfile artist={selectedProfile as IArtistProfile} onChat={handleOpenChat} onBook={() => navigate('/booking')} isOwnProfile={selectedProfile.id === currentUser.id} isBlocked={isUserBlocked} onUpdateProfile={handleUpdateUserProfile} initialTab={profileTab} />} />
          <Route path="/analytics" element={(currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SYSTEM_ADMIN_LIVE) ? <AnalyticsDashboard /> : <div>Access Denied</div>} />
          <Route path="/announcements_internal" element={<Announcements onBack={() => navigate('/dashboard')} />} />
          <Route path="/pending" element={<PendingPage />} />
          <Route path="/dashboard" element={<Dashboard user={transformRosterMemberToArtistProfile(currentUser, MOCK_ARTIST_PROFILE)} onNavigate={navigate} />} />
          <Route path="/" element={<Dashboard user={transformRosterMemberToArtistProfile(currentUser, MOCK_ARTIST_PROFILE)} onNavigate={navigate} />} />
        </Routes>
      </div>
    );
  };

  if (authLoading) {
    return <PageLoader />;
  }

  if (isUserBlocked || currentUser?.status === UserStatus.SUSPENDED) {
    return <BlockedScreen onAppeal={handleAppeal} />;
  }
  
  // Consolidated loading modal for all auth types
  if (isConnecting || isWalletLoggingIn || isWeb2LoggingIn) {
    return (
      <div className="fixed inset-0 bg-kala-900 bg-opacity-80 flex items-center justify-center z-50">
        <div className="bg-kala-800 p-8 rounded-lg text-center max-w-sm">
          <h2 className="text-xl font-bold text-white mb-4">Authenticating...</h2>
          <p className="text-kala-400 mb-6 text-sm">
            Please wait. Your request is being processed.
          </p>
          <Loader2 className="w-8 h-8 text-kala-secondary animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (!currentUser) {
    if (joinRequestSubmitted) {
      return (
        <div className="min-h-screen bg-kala-900 flex flex-col items-center justify-center p-4 text-center">
          <div className="bg-green-900/20 p-8 rounded-full border-4 border-green-500 mb-8">
            <CheckCircle className="w-24 h-24 text-green-500" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Application Submitted!</h1>
          <p className="text-green-200 text-lg max-w-xl mx-auto">
            Thank you for your interest in joining KalaKrut. Your application is now under review by our admin team. You will be notified via email once a decision has been made.
          </p>
          <button
            onClick={() => {
              setJoinRequestSubmitted(false);
              setIsNewWeb3User(false);
              navigate('/');
            }}
            className="mt-8 px-8 py-3 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-xl border border-kala-500 transition-colors"
          >
            Back to Home
          </button>
        </div>
      );
    }
    if (location.pathname === '/announcements_public') {
      return (
          <Announcements onBack={() => navigate('/')} />
      );
    }
    if (location.pathname === '/join_request') {
      return (
        <div className="min-h-screen bg-kala-900 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <button
              onClick={() => {
                setIsNewWeb3User(false);
                navigate('/');
              }}
              className="flex items-center gap-2 text-kala-400 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </button>
              <JoinForm
                onComplete={() => {
                  setJoinRequestSubmitted(true);
                  notify('Your request has been submitted!', 'success');
                }}
                onBlockUser={handleBlockUser}
              />
          </div>
        </div>
      );
    }
    return (
      <Home
        onLogin={handleLogin}
        onViewNews={() => navigate('/announcements_public')}
        onJoin={() => {
          setIsNewWeb3User(true);
          navigate('/join_request');
        }}
      />
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
    <Layout
      currentView={location.pathname}
      userRole={currentUser?.role || UserRole.GUEST}
      onNavigate={navigate}
      onLogout={logout} // DEFINITIVE FIX: Pass the REAL logout function to the layout
    >
      {showChat && (
        <ChatOverlay
          recipientName={chatRecipient.name || ''}
          recipientAvatar={chatRecipient.avatar || ''}
          onClose={() => setShowChat(false)}
          onNavigateToBooking={() => {
            setShowChat(false);
            navigate('/booking');
          }}
          onBlockUser={handleBlockUser}
        />
      )}
      {showTokenExchange && (
        <TokenExchange onClose={() => setShowTokenExchange(false)} />
      )}
      {location.pathname !== '/dashboard' && <UserWidget />}
      {renderAppContent()}
    </Layout>
    </DndProvider>
  );
};

const App: React.FC = () => (
  <Routes>
    <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
    <Route path="*" element={<AppContent />} />
  </Routes>
);

export default App;
